// src/components/AnalysisResult.tsx

import React, { useState } from "react";
import type { AnalyzeEtfResponse, Recommendation, SupplementalContext } from "../api/myTypes";
import LLMDialogLog from "./LLMDialogLog";
import { trackRecommendation, trackPosition } from "../api/etfApi";

interface SupplementalContextSectionProps {
  data: SupplementalContext;
}

const SupplementalContextSection: React.FC<SupplementalContextSectionProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(true);  // Expanded by default so content is visible
  const hasBollinger = data?.bollinger && (data.bollinger.regime != null || data.bollinger.bandwidth != null);
  const hasLR = data?.linear_regression && (data.linear_regression.lr20 || data.linear_regression.lr60);
  const hasData = hasBollinger || hasLR;

  if (!hasData) {
    return (
      <div className="card" style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
        <div style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Supplemental Context (BB/LR): Not enough data</div>
      </div>
    );
  }

  const bb = data.bollinger;
  const lr = data.linear_regression;
  const combined = lr?.combined_context;

  return (
    <div className="card" style={{ marginBottom: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem",
          background: "transparent",
          border: "none",
          color: "#ffffff",
          fontSize: "1rem",
          fontWeight: "600",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span>Supplemental Context (BB/LR)</span>
        <span style={{ fontSize: "1.25rem", transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
      </button>
      {expanded && (
        <div style={{ padding: "0 1rem 1rem", borderTop: "1px solid #2c3142" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginTop: "1rem" }}>
            {/* Bollinger Bands */}
            {hasBollinger && bb && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem", fontWeight: "600" }}>Bollinger Bands</div>
                {bb.regime != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Regime:</span>{" "}
                    <span style={{ color: bb.regime === "squeeze" ? "#f59e0b" : bb.regime === "expanded" ? "#3b82f6" : "#9ca3af" }}>{bb.regime}</span>
                  </div>
                )}
                {bb.bandwidth_percentile_60 != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Bandwidth %ile (60):</span> {bb.bandwidth_percentile_60.toFixed(1)}%
                  </div>
                )}
                {bb.percent_b != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>%B:</span> {bb.percent_b.toFixed(2)}
                  </div>
                )}
                {bb.extension_flag && bb.extension_flag !== "none" && (
                  <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem" }}>Extension: {bb.extension_flag.replace("_", " ")}</div>
                )}
                {bb.trend_ride_flag && bb.trend_ride_flag !== "none" && (
                  <div style={{ fontSize: "0.75rem", color: "#3b82f6", marginTop: "0.25rem" }}>Trend ride: {bb.trend_ride_flag.replace("_", " ")}</div>
                )}
              </div>
            )}
            {/* LR20 */}
            {lr?.lr20 && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem", fontWeight: "600" }}>LR20</div>
                {lr.lr20.direction != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Direction:</span>{" "}
                    <span style={{ color: lr.lr20.direction === "up" ? "#22c55e" : lr.lr20.direction === "down" ? "#ef4444" : "#9ca3af" }}>{lr.lr20.direction}</span>
                  </div>
                )}
                {lr.lr20.trend_score != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Trend score:</span> {lr.lr20.trend_score}
                  </div>
                )}
                {lr.lr20.r2 != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>R²:</span> {lr.lr20.r2.toFixed(2)}
                  </div>
                )}
                {lr.lr20.z_from_line != null && (
                  <div style={{ fontSize: "0.875rem" }}>
                    <span style={{ color: "#9ca3af" }}>z from line:</span> {lr.lr20.z_from_line.toFixed(2)}
                  </div>
                )}
              </div>
            )}
            {/* LR60 */}
            {lr?.lr60 && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem", fontWeight: "600" }}>LR60</div>
                {lr.lr60.direction != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Direction:</span>{" "}
                    <span style={{ color: lr.lr60.direction === "up" ? "#22c55e" : lr.lr60.direction === "down" ? "#ef4444" : "#9ca3af" }}>{lr.lr60.direction}</span>
                  </div>
                )}
                {lr.lr60.trend_score != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Trend score:</span> {lr.lr60.trend_score}
                  </div>
                )}
                {lr.lr60.r2 != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>R²:</span> {lr.lr60.r2.toFixed(2)}
                  </div>
                )}
                {lr.lr60.z_from_line != null && (
                  <div style={{ fontSize: "0.875rem" }}>
                    <span style={{ color: "#9ca3af" }}>z from line:</span> {lr.lr60.z_from_line.toFixed(2)}
                  </div>
                )}
              </div>
            )}
            {/* Combined context */}
            {combined && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem", fontWeight: "600" }}>Combined</div>
                {combined.trend_alignment != null && (
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem" }}>
                    <span style={{ color: "#9ca3af" }}>Alignment:</span> {combined.trend_alignment.replace("_", " ")}
                  </div>
                )}
                {combined.chop_risk_flag === true && (
                  <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem" }}>Chop risk elevated</div>
                )}
                {combined.overextension_flag && combined.overextension_flag !== "none" && (
                  <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.25rem" }}>Extension: {combined.overextension_flag.replace("_", " ")}</div>
                )}
              </div>
            )}
          </div>
          {/* Notes */}
          {((bb?.notes && bb.notes.length > 0) || (combined?.notes && combined.notes.length > 0)) && (
            <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #374151" }}>
              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem", fontWeight: "600" }}>Notes</div>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.875rem", color: "#ffffff", lineHeight: 1.5 }}>
                {[...(bb?.notes || []), ...(combined?.notes || [])].slice(0, 5).map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface AnalysisResultProps {
  data: AnalyzeEtfResponse;
  dialogButton?: React.ReactNode;
}

const AnalysisResult: React.FC<AnalysisResultProps> = ({ data, dialogButton }) => {
  const rec: Recommendation | undefined = data.recommendation;
  const marketRegime = data.market_regime;
  const [trackingStatus, setTrackingStatus] = useState<{ type: "recommendation" | "position" | null; message: string }>({ type: null, message: "" });
  
  // Debug logging when data changes
  React.useEffect(() => {
    if (data.strategy_result?.signals) {
      const rsi14Signal = data.strategy_result.signals.find((s: any) => s.name === "RSI14");
      if (rsi14Signal) {
        console.log(`[AnalysisResult] RSI14 signal received:`, rsi14Signal);
      }
    }
    if (data.supplemental_context) {
      console.log(`[AnalysisResult] Supplemental context received:`, data.supplemental_context);
    } else {
      console.log(`[AnalysisResult] No supplemental_context in response`);
    }
  }, [data]);

  if (!rec) {
    return (
      <div className="card">
        <div className="card-body">No recommendation returned.</div>
      </div>
    );
  }

  const handleTrackRecommendation = async () => {
    try {
      console.log("Track Trade button clicked");
      const recommendationId = `rec_${data.ticker}_${Date.now()}`;
      
      // Extract entry price from entry_zone if possible
      const entryPriceMatch = rec.entry_zone?.match(/\$?([\d.]+)/);
      const entryPrice = entryPriceMatch ? parseFloat(entryPriceMatch[1]) : undefined;
      
      console.log("Tracking recommendation:", { recommendationId, ticker: data.ticker, entryPrice });
      
      await trackRecommendation({
        recommendationId,
        ticker: data.ticker,
        strategy: "single_etf",
        model: data.analysis_model || "classic",
        entryPrice: entryPrice,
        recommendationData: rec,
        marketRegime: marketRegime || undefined,
      });
      
      console.log("Recommendation tracked successfully");
      setTrackingStatus({ type: "recommendation", message: "✓ Recommendation tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Performance tab
      window.dispatchEvent(new CustomEvent('refresh-performance'));
    } catch (error: any) {
      console.error("Error tracking recommendation:", error);
      setTrackingStatus({ type: "recommendation", message: `⚠️ Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  // Helper function to extract price from various formats
  // Returns the average if it's a range, otherwise the single value
  const extractPrice = (priceStr: string | null | undefined, useAverage: boolean = true): number | undefined => {
    if (!priceStr) return undefined;
    
    // Try to extract number from various formats:
    // "$100", "100", "$100-105", "100-105", "$100.50", "100.50", "around $100", etc.
    
    // First, try to match a range pattern
    const rangePattern = /\$?\s*([\d,]+\.?\d*)\s*-\s*\$?\s*([\d,]+\.?\d*)/;
    const rangeMatch = priceStr.match(rangePattern);
    
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      // It's a range - calculate average if useAverage is true, otherwise use lower value
      const lower = parseFloat(rangeMatch[1].replace(/,/g, ''));
      const upper = parseFloat(rangeMatch[2].replace(/,/g, ''));
      
      if (!isNaN(lower) && !isNaN(upper) && lower > 0 && upper > 0 && upper >= lower) {
        return useAverage ? (lower + upper) / 2 : lower;
      }
    }
    
    // If not a range, try single number pattern
    const singlePattern = /\$?\s*([\d,]+\.?\d*)/;
    const singleMatch = priceStr.match(singlePattern);
    
    if (singleMatch && singleMatch[1]) {
      const priceValue = singleMatch[1].replace(/,/g, '');
      const parsed = parseFloat(priceValue);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    
    return undefined;
  };

  const handleTrackPosition = async () => {
    try {
      const positionId = `pos_${data.ticker}_${Date.now()}`;
      const recommendationId = `rec_${data.ticker}_${Date.now()}`;
      
      // Try multiple sources for entry price (use average for ranges)
      let entryPrice: number | undefined = undefined;
      
      // 1. Try entry_zone field (PRIORITY - use average if range)
      if (rec.entry_zone) {
        entryPrice = extractPrice(rec.entry_zone, true); // true = use average for ranges
      }
      
      // 2. Try current price from asset profile as fallback
      if (!entryPrice && data.asset_profile?.price_context?.candles?.length > 0) {
        const candles = data.asset_profile.price_context.candles;
        const latestCandle = candles[candles.length - 1];
        if (latestCandle?.close) {
          entryPrice = typeof latestCandle.close === 'number' 
            ? latestCandle.close 
            : parseFloat(String(latestCandle.close));
        }
      }
      
      // 3. Try extracting from text_reasoning if it contains price info
      if (!entryPrice && rec.text_reasoning) {
        entryPrice = extractPrice(rec.text_reasoning, true);
      }
      
      // Extract stop loss and take profit (use average for ranges)
      const stopLoss = extractPrice(rec.stop_loss, true);
      const takeProfit = extractPrice(rec.take_profit, true);

      if (!entryPrice || isNaN(entryPrice)) {
        setTrackingStatus({ 
          type: "position", 
          message: "Error: Could not extract entry price. Please ensure the recommendation includes an entry zone or price." 
        });
        setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
        return;
      }

      await trackPosition({
        positionId,
        recommendationId,
        ticker: data.ticker,
        entryPrice,
        stopLoss,
        takeProfit,
      });
      setTrackingStatus({ type: "position", message: "✓ Position tracked successfully!" });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
      // Dispatch event to refresh Positions tab
      window.dispatchEvent(new CustomEvent('refresh-positions'));
    } catch (error: any) {
      setTrackingStatus({ type: "position", message: `Error: ${error.message}` });
      setTimeout(() => setTrackingStatus({ type: null, message: "" }), 5000);
    }
  };

  // Get regime badge color
  const getRegimeBadgeColor = (regimeType: string) => {
    if (regimeType?.includes("Bull")) return "#10b981"; // green
    if (regimeType?.includes("Bear")) return "#ef4444"; // red
    if (regimeType?.includes("Choppy") || regimeType?.includes("Sideways")) return "#f59e0b"; // amber
    if (regimeType?.includes("High Volatility")) return "#8b5cf6"; // purple
    if (regimeType?.includes("Low Volatility")) return "#3b82f6"; // blue
    return "#6b7280"; // gray
  };

  // Check if this is SOXL Tactical strategy
  const isSoxlTactical = data.analysis_model === "soxl_tactical_10m";
  // Check if this is Triple Leveraged BB LRC Scale strategy
  const isTripleLeveragedBBLRC = data.analysis_model === "triple_leveraged_bb_lrc_scale";
  const strategyResult = data.strategy_result;

  return (
    <div className="analysis-grid">
      {/* SOXL Tactical Strategy Result */}
      {isSoxlTactical && strategyResult && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1.5rem", backgroundColor: "#1a1d29", border: "2px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1rem" }}>
            SOXL Tactical Strategy Analysis
          </h3>
          
          {/* Action, Grade, Score */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            {strategyResult.action && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Action</div>
                <div style={{ 
                  fontSize: "1.5rem", 
                  fontWeight: "bold",
                  color: strategyResult.action === "BUY" || strategyResult.action === "BUY_MORE" ? "#22c55e" :
                         strategyResult.action === "SELL" ? "#ef4444" : "#f59e0b"
                }}>
                  {strategyResult.action}
                </div>
              </div>
            )}
            
            {strategyResult.grade && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px", position: "relative" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>
                  Setup Quality
                </div>
                <div style={{ 
                  fontSize: "1.5rem", 
                  fontWeight: "bold",
                  color: strategyResult.grade === "A+" ? "#22c55e" :
                         strategyResult.grade === "A" ? "#10b981" :
                         strategyResult.grade === "B" ? "#3b82f6" :
                         strategyResult.grade === "C" ? "#f59e0b" : "#6b7280",
                  marginBottom: "0.5rem"
                }}>
                  {strategyResult.grade}
                  <span style={{ 
                    fontSize: "0.875rem", 
                    fontWeight: "normal", 
                    marginLeft: "0.5rem",
                    color: "#9ca3af"
                  }}>
                    - {strategyResult.grade_description || 
                        (strategyResult.grade === "A+" ? "Excellent Setup" :
                         strategyResult.grade === "A" ? "Good Setup" :
                         strategyResult.grade === "B" ? "Fair Setup" :
                         strategyResult.grade === "C" ? "Weak Setup" : "Avoid Trade")}
                  </span>
                </div>
                {(strategyResult.grade_meaning || (() => {
                  // Fallback meanings if not provided
                  if (strategyResult.grade === "A+") return "Strong buy signal with high-quality setup, confirmed entry, and favorable conditions";
                  if (strategyResult.grade === "A") return "Good buy signal with quality setup and favorable conditions";
                  if (strategyResult.grade === "B") return "Moderate quality setup - consider with caution, may need additional confirmation";
                  if (strategyResult.grade === "C") return "Low quality setup - high risk, consider waiting for better conditions";
                  return "Poor setup quality - not recommended for entry, wait for better conditions";
                })()) && (
                  <div style={{ fontSize: "0.75rem", color: "#9ca3af", lineHeight: "1.3", marginTop: "0.25rem" }}>
                    {strategyResult.grade_meaning || 
                     (strategyResult.grade === "A+" ? "Strong buy signal with high-quality setup, confirmed entry, and favorable conditions" :
                      strategyResult.grade === "A" ? "Good buy signal with quality setup and favorable conditions" :
                      strategyResult.grade === "B" ? "Moderate quality setup - consider with caution, may need additional confirmation" :
                      strategyResult.grade === "C" ? "Low quality setup - high risk, consider waiting for better conditions" :
                      "Poor setup quality - not recommended for entry, wait for better conditions")}
                  </div>
                )}
              </div>
            )}
            
            {strategyResult.score_total !== undefined && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>
                  Quality Score
                </div>
                <div style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#ffffff", marginBottom: "0.5rem" }}>
                  {strategyResult.score_total}/100
                </div>
                {(() => {
                  // Simple interpretation based on score
                  let scoreInterpretation = "";
                  let scoreColor = "#9ca3af";
                  if (strategyResult.score_total >= 85) {
                    scoreInterpretation = "Excellent - High quality setup";
                    scoreColor = "#22c55e";
                  } else if (strategyResult.score_total >= 70) {
                    scoreInterpretation = "Good - Quality setup";
                    scoreColor = "#10b981";
                  } else if (strategyResult.score_total >= 55) {
                    scoreInterpretation = "Fair - Moderate quality";
                    scoreColor = "#3b82f6";
                  } else if (strategyResult.score_total >= 40) {
                    scoreInterpretation = "Weak - Low quality";
                    scoreColor = "#f59e0b";
                  } else {
                    scoreInterpretation = "Poor - Avoid trade";
                    scoreColor = "#6b7280";
                  }
                  return (
                    <div style={{ fontSize: "0.75rem", color: scoreColor, fontStyle: "italic", marginTop: "0.25rem" }}>
                      {scoreInterpretation}
                    </div>
                  );
                })()}
                {/* Score Breakdown */}
                {strategyResult.score_breakdown && (
                  <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #374151", fontSize: "0.7rem" }}>
                    <div style={{ color: "#9ca3af", marginBottom: "0.25rem" }}>Score Breakdown:</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                      {strategyResult.score_breakdown.regime_points !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#9ca3af" }}>Regime:</span>
                          <span style={{ color: strategyResult.score_breakdown.regime_points > 0 ? "#22c55e" : "#6b7280" }}>
                            {strategyResult.score_breakdown.regime_points > 0 ? "+" : ""}{strategyResult.score_breakdown.regime_points}
                          </span>
                        </div>
                      )}
                      {strategyResult.score_breakdown.setup_points !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#9ca3af" }}>Setup:</span>
                          <span style={{ color: strategyResult.score_breakdown.setup_points > 0 ? "#22c55e" : "#6b7280" }}>
                            {strategyResult.score_breakdown.setup_points > 0 ? "+" : ""}{strategyResult.score_breakdown.setup_points}
                          </span>
                        </div>
                      )}
                      {strategyResult.score_breakdown.momentum_points !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#9ca3af" }}>Momentum:</span>
                          <span style={{ color: strategyResult.score_breakdown.momentum_points > 0 ? "#22c55e" : "#6b7280" }}>
                            {strategyResult.score_breakdown.momentum_points > 0 ? "+" : ""}{strategyResult.score_breakdown.momentum_points}
                          </span>
                        </div>
                      )}
                      {strategyResult.score_breakdown.confirmation_points !== undefined && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#9ca3af" }}>Confirmation:</span>
                          <span style={{ color: strategyResult.score_breakdown.confirmation_points > 0 ? "#22c55e" : "#6b7280" }}>
                            {strategyResult.score_breakdown.confirmation_points > 0 ? "+" : ""}{strategyResult.score_breakdown.confirmation_points}
                          </span>
                        </div>
                      )}
                      {strategyResult.score_breakdown.penalty_points !== undefined && strategyResult.score_breakdown.penalty_points < 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ color: "#9ca3af" }}>Penalties:</span>
                          <span style={{ color: "#ef4444" }}>
                            {strategyResult.score_breakdown.penalty_points}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {strategyResult.regime && (
              <div style={{ padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
                <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Regime</div>
                <div style={{ 
                  fontSize: "1.5rem", 
                  fontWeight: "bold",
                  color: strategyResult.regime === "BULL" ? "#22c55e" :
                         strategyResult.regime === "BEAR" ? "#ef4444" : "#f59e0b"
                }}>
                  {strategyResult.regime}
                </div>
              </div>
            )}
          </div>

          {/* Setup Type */}
          {strategyResult.setup_type && strategyResult.setup_type !== "NONE" && (
            <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "#2c3142", borderRadius: "6px" }}>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", marginBottom: "0.25rem" }}>Setup Type</div>
              <div style={{ fontSize: "1rem", fontWeight: "600", color: "#ffffff" }}>{strategyResult.setup_type}</div>
            </div>
          )}

          {/* Key Levels */}
          {strategyResult.key_levels && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Key Levels</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "0.75rem", fontSize: "0.875rem" }}>
                {strategyResult.key_levels.ema20 !== null && strategyResult.key_levels.ema20 !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>EMA20</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.key_levels.ema20.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.key_levels.ema50 !== null && strategyResult.key_levels.ema50 !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>EMA50</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.key_levels.ema50.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.key_levels.sma200 !== null && strategyResult.key_levels.sma200 !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>SMA200</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.key_levels.sma200.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.key_levels.hard_stop_price !== null && strategyResult.key_levels.hard_stop_price !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>Hard Stop</div>
                    <div style={{ color: "#ef4444", fontWeight: "600" }}>${strategyResult.key_levels.hard_stop_price.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.key_levels.prior_day_high !== null && strategyResult.key_levels.prior_day_high !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>Prior Day High</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.key_levels.prior_day_high.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.key_levels.prior_day_low !== null && strategyResult.key_levels.prior_day_low !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>Prior Day Low</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.key_levels.prior_day_low.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Signals */}
          {strategyResult.signals && Array.isArray(strategyResult.signals) && strategyResult.signals.length > 0 && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Signals</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {strategyResult.signals.map((signal: any, idx: number) => {
                  // Special handling for RSI14, MACD Histogram, and Setup Type
                  const isRSI14 = signal.name === "RSI14";
                  const isMACDHist = signal.name === "MACD Histogram";
                  const isSetupType = signal.name === "Setup Type";
                  let displayValue: string;
                  
                  if (isRSI14 && signal.value !== null && signal.value !== undefined) {
                    // Show numeric value with 2 decimals (same as Auto Cycle display)
                    const roundedValue = Number(signal.value).toFixed(2);
                    // Debug logging to verify value is received
                    console.log(`[RSI14 Display] Received value: ${signal.value}, Display: ${roundedValue}`);
                    if (signal.status) {
                      displayValue = `${signal.status} - ${roundedValue}`;
                    } else {
                      displayValue = roundedValue;
                    }
                  } else if (isMACDHist && signal.status && signal.value !== null && signal.value !== undefined) {
                    // Show status text with numeric value: "Bullish momentum (strengthening) - 0.0234"
                    const roundedValue = Number(signal.value).toFixed(4);
                    displayValue = `${signal.status} - ${roundedValue}`;
                  } else if (isSetupType && signal.status) {
                    // Show status text for Setup Type: "Entry warranted - Pullback to EMA20"
                    displayValue = signal.status;
                  } else if ((isRSI14 || isMACDHist) && signal.status) {
                    // Fallback if value is missing
                    displayValue = signal.status;
                  } else {
                    // For other signals, show the value
                    displayValue = signal.value !== null && signal.value !== undefined 
                      ? String(signal.value) 
                      : "N/A";
                  }
                  
                  const displayColor = (isRSI14 || isMACDHist || isSetupType) && signal.status_color 
                    ? signal.status_color 
                    : (signal.passed ? "#10b981" : "#ef4444");
                  
                  return (
                  <div key={idx} style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "0.5rem",
                    backgroundColor: signal.passed ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)",
                    borderRadius: "4px"
                  }}>
                    <div>
                      <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff" }}>{signal.name}</div>
                      {signal.note && !isRSI14 && !isMACDHist && !isSetupType && (
                        <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>{signal.note}</div>
                      )}
                      {isSetupType && signal.description && (
                        <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "0.25rem", fontStyle: "italic" }}>
                          {signal.description}
                        </div>
                      )}
                      {isSetupType && signal.entry_warranted && (
                        <div style={{ 
                          fontSize: "0.7rem", 
                          color: "#22c55e", 
                          marginTop: "0.25rem",
                          fontWeight: "600"
                        }}>
                          ✓ Entry Confirmed
                        </div>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: "0.875rem", 
                      fontWeight: "600",
                      color: displayColor,
                      textAlign: "right",
                      maxWidth: "60%"
                    }}>
                      {displayValue}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Position Info */}
          {strategyResult.position && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Position</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.75rem", fontSize: "0.875rem" }}>
                <div>
                  <div style={{ color: "#9ca3af" }}>Entry Price</div>
                  <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.position.entry_price?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af" }}>Shares</div>
                  <div style={{ color: "#ffffff", fontWeight: "600" }}>{strategyResult.position.shares?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af" }}>Current Price</div>
                  <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.position.current_price?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: "#9ca3af" }}>Unrealized P&L</div>
                  <div style={{ 
                    color: (strategyResult.position.unrealized_pnl_$ || 0) >= 0 ? "#22c55e" : "#ef4444", 
                    fontWeight: "600" 
                  }}>
                    ${strategyResult.position.unrealized_pnl_$?.toFixed(2)} ({strategyResult.position.unrealized_pnl_pct?.toFixed(2)}%)
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sell Info */}
          {strategyResult.sell && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "rgba(239, 68, 68, 0.1)", borderRadius: "8px", border: "1px solid #ef4444" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ef4444", marginBottom: "0.75rem" }}>Sell Recommendation</div>
              <div style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem" }}>
                <strong>Urgency:</strong> {strategyResult.sell.sell_urgency}
              </div>
              <div style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem" }}>
                <strong>Sell {strategyResult.sell.suggested_sell_pct}%</strong> ({strategyResult.sell.sell_amount_shares} shares)
              </div>
              {strategyResult.sell.sell_plan && (
                <div style={{ fontSize: "0.875rem", color: "#ffffff", marginTop: "0.5rem", fontStyle: "italic" }}>
                  {strategyResult.sell.sell_plan}
                </div>
              )}
            </div>
          )}

          {/* Buy More Info */}
          {strategyResult.buy_more && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "rgba(34, 197, 94, 0.1)", borderRadius: "8px", border: "1px solid #22c55e" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#22c55e", marginBottom: "0.75rem" }}>Add to Position</div>
              <div style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem" }}>
                <strong>Add {strategyResult.buy_more.suggested_add_pct}%</strong> ({strategyResult.buy_more.suggested_add_shares} shares)
              </div>
              {strategyResult.buy_more.add_plan && (
                <div style={{ fontSize: "0.875rem", color: "#ffffff", marginTop: "0.5rem", fontStyle: "italic" }}>
                  {strategyResult.buy_more.add_plan}
                </div>
              )}
            </div>
          )}

          {/* Trade Plan */}
          {/* Sell Targets - Show when position is held */}
          {strategyResult.sell_targets && strategyResult.has_position && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px", border: "1px solid #10b981" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#10b981", marginBottom: "0.75rem" }}>Sell Targets (Exit Strategy)</div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {strategyResult.sell_targets.tp1_price && (() => {
                  // Calculate if TP1 is reached (fallback if backend flag is missing)
                  const tp1Reached = strategyResult.sell_targets.tp1_reached === true || 
                                     strategyResult.sell_targets.tp1_reached === "true" ||
                                     (strategyResult.position && strategyResult.sell_targets.current_price && 
                                      strategyResult.sell_targets.current_price >= strategyResult.sell_targets.tp1_price);
                  return (
                    <div style={{ padding: "0.5rem", backgroundColor: "#1a1d29", borderRadius: "4px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong style={{ color: "#10b981" }}>TP1 (10% Target):</strong>
                          <div style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "600", marginTop: "0.25rem" }}>
                            ${strategyResult.sell_targets.tp1_price.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: "#9ca3af", fontSize: "0.75rem" }}>Sell 50%</div>
                          {strategyResult.position && (
                            <div style={{ color: tp1Reached ? "#10b981" : "#9ca3af", fontSize: "0.75rem", marginTop: "0.25rem" }}>
                              {tp1Reached ? "✓ Target reached" : `${strategyResult.sell_targets.tp1_remaining_pct?.toFixed(1) || (10 - (strategyResult.sell_targets.distance_to_tp1_pct || 0)).toFixed(1)}% to go`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ color: "#6b7280", fontSize: "0.7rem", marginTop: "0.25rem", fontStyle: "italic" }}>
                        {strategyResult.sell_targets.tp1_description}
                      </div>
                    </div>
                  );
                })()}
                {strategyResult.sell_targets.tp2_price && (() => {
                  // Calculate if TP2 is reached (fallback if backend flag is missing)
                  const tp2Reached = strategyResult.sell_targets.tp2_reached === true || 
                                     strategyResult.sell_targets.tp2_reached === "true" ||
                                     (strategyResult.position && strategyResult.sell_targets.current_price && 
                                      strategyResult.sell_targets.current_price >= strategyResult.sell_targets.tp2_price);
                  return (
                    <div style={{ padding: "0.5rem", backgroundColor: "#1a1d29", borderRadius: "4px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong style={{ color: "#10b981" }}>TP2 (20% Target):</strong>
                          <div style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "600", marginTop: "0.25rem" }}>
                            ${strategyResult.sell_targets.tp2_price.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: "#9ca3af", fontSize: "0.75rem" }}>Sell remaining 50%</div>
                          {strategyResult.position && (
                            <div style={{ color: tp2Reached ? "#10b981" : "#9ca3af", fontSize: "0.75rem", marginTop: "0.25rem" }}>
                              {tp2Reached ? "✓ Target reached" : `${strategyResult.sell_targets.tp2_remaining_pct?.toFixed(1) || (20 - (strategyResult.sell_targets.distance_to_tp2_pct || 0)).toFixed(1)}% to go`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ color: "#6b7280", fontSize: "0.7rem", marginTop: "0.25rem", fontStyle: "italic" }}>
                        {strategyResult.sell_targets.tp2_description}
                      </div>
                    </div>
                  );
                })()}
                {strategyResult.position && (
                  <div style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid #374151", fontSize: "0.75rem", color: "#9ca3af" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Entry Price:</span>
                      <span style={{ color: "#ffffff" }}>${strategyResult.position.entry_price.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.25rem" }}>
                      <span>Current Price:</span>
                      <span style={{ color: "#ffffff" }}>${strategyResult.position.current_price.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.25rem" }}>
                      <span>Current Gain:</span>
                      <span style={{ color: strategyResult.position.unrealized_pnl_pct >= 0 ? "#10b981" : "#ef4444" }}>
                        {strategyResult.position.unrealized_pnl_pct >= 0 ? "+" : ""}{strategyResult.position.unrealized_pnl_pct.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {strategyResult.trade_plan && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Trade Plan</div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                {/* Display Rules:
                    - Entry Reference: Always shown (indicator reference point, e.g., "EMA20: $50.00")
                    - Next Entry Zone: Shown when available (actionable entry guidance with price range)
                    - Next Entry Price: Only shown if zone is NOT available (fallback when zone can't be calculated)
                    Note: If Next Entry Zone is shown, it already contains the ideal price, so Next Entry Price is redundant
                */}
                {strategyResult.trade_plan.entry_reference && (
                  <div><strong style={{ color: "#ffffff" }}>Entry Reference:</strong> {strategyResult.trade_plan.entry_reference}</div>
                )}
                {strategyResult.trade_plan.next_entry_zone ? (
                  <div>
                    <strong style={{ color: "#ffffff" }}>Next Entry Zone:</strong> {strategyResult.trade_plan.next_entry_zone}
                    {strategyResult.trade_plan.entry_zone_type && (
                      <span style={{ color: "#9ca3af", fontSize: "0.75rem", marginLeft: "0.5rem", fontStyle: "italic" }}>
                        ({strategyResult.trade_plan.entry_zone_type === "moving_target" ? "Moving target - updates with indicators" : strategyResult.trade_plan.entry_zone_type})
                      </span>
                    )}
                  </div>
                ) : (
                  /* Fallback: Show Next Entry Price only if zone is not available */
                  strategyResult.trade_plan.next_entry_price && (
                    <div>
                      <strong style={{ color: "#ffffff" }}>Next Entry Price:</strong> ${strategyResult.trade_plan.next_entry_price.toFixed(2)}
                      {strategyResult.trade_plan.entry_zone_type && (
                        <span style={{ color: "#9ca3af", fontSize: "0.75rem", marginLeft: "0.5rem", fontStyle: "italic" }}>
                          ({strategyResult.trade_plan.entry_zone_type === "moving_target" ? "Moving target" : strategyResult.trade_plan.entry_zone_type})
                        </span>
                      )}
                    </div>
                  )
                )}
                {strategyResult.trade_plan.stop_logic && (
                  <div><strong style={{ color: "#ffffff" }}>Stop:</strong> {strategyResult.trade_plan.stop_logic}</div>
                )}
                {strategyResult.trade_plan.tp1 && (
                  <div><strong style={{ color: "#ffffff" }}>TP1:</strong> {strategyResult.trade_plan.tp1}</div>
                )}
                {strategyResult.trade_plan.tp2 && (
                  <div><strong style={{ color: "#ffffff" }}>TP2:</strong> {strategyResult.trade_plan.tp2}</div>
                )}
                {strategyResult.trade_plan.sizing_hint && (
                  <div><strong style={{ color: "#ffffff" }}>Sizing:</strong> {strategyResult.trade_plan.sizing_hint}</div>
                )}
              </div>
            </div>
          )}

          {/* No Trade Reasons */}
          {strategyResult.no_trade_reasons && Array.isArray(strategyResult.no_trade_reasons) && strategyResult.no_trade_reasons.length > 0 && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "rgba(245, 158, 11, 0.1)", borderRadius: "8px", border: "1px solid #f59e0b" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#f59e0b", marginBottom: "0.5rem" }}>No Trade Reasons</div>
              <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.875rem", color: "#ffffff" }}>
                {strategyResult.no_trade_reasons.map((reason: string, idx: number) => (
                  <li key={idx}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Supplemental Context (BB/LR) - show prominently before main recommendation */}
      {data.supplemental_context ? (
        <SupplementalContextSection data={data.supplemental_context} />
      ) : (
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <div style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Supplemental Context (BB/LR): Not available</div>
        </div>
      )}

      {/* Market Regime Badge */}
      {marketRegime && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#1a1d29", border: "1px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1rem", fontWeight: "bold", marginBottom: "0.75rem" }}>
            Market Regime Analysis
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <span
              style={{
                display: "inline-block",
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                backgroundColor: getRegimeBadgeColor(marketRegime.regime_type),
                color: "white",
                fontWeight: "bold",
                fontSize: "0.875rem"
              }}
            >
              {marketRegime.regime_type}
            </span>
            <span style={{ fontSize: "0.875rem", color: "#ffffff" }}>
              Confidence: {marketRegime.regime_confidence}
            </span>
          </div>
          <p style={{ fontSize: "0.875rem", color: "#ffffff", marginBottom: "0.5rem", lineHeight: "1.5" }}>
            {marketRegime.regime_description}
          </p>
          {marketRegime.strategy_adjustments && (
            <div style={{ fontSize: "0.8rem", color: "#ffffff", fontStyle: "italic" }}>
              <strong>Strategy Adjustment:</strong> {marketRegime.strategy_adjustments}
            </div>
          )}
        </div>
      )}

      <div className="card main-rec-card p-4 bg-white rounded-xl shadow">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 className="font-bold text-lg">AI Trade Recommendation</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              className="primary-button"
              onClick={handleTrackRecommendation}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Trade
            </button>
            <button
              className="primary-button"
              onClick={handleTrackPosition}
              style={{ fontSize: "0.875rem", padding: "0.5rem 1rem" }}
            >
              Track Position
            </button>
          </div>
        </div>
        {trackingStatus.type && (
          <div style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            borderRadius: "6px",
            backgroundColor: trackingStatus.message.includes("Error") ? "#fee2e2" : "#d1fae5",
            color: trackingStatus.message.includes("Error") ? "#991b1b" : "#065f46",
            fontSize: "0.95rem",
            fontWeight: "500",
            border: `2px solid ${trackingStatus.message.includes("Error") ? "#fca5a5" : "#86efac"}`,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            <span style={{ fontSize: "1.2rem" }}>
              {trackingStatus.message.includes("Error") ? "⚠️" : "✓"}
            </span>
            <span>{trackingStatus.message}</span>
          </div>
        )}
        {rec.rating && <div><strong>Rating:</strong> {rec.rating}</div>}
        {rec.conviction && <div><strong>Conviction:</strong> {rec.conviction}</div>}
        {rec.entry_zone && <div><strong>Buy/Entry Zone:</strong> {rec.entry_zone}</div>}
        {rec.exit_zone && <div><strong>Sell/Exit Zone:</strong> {rec.exit_zone}</div>}
        {rec.position_size && <div><strong>Position Size:</strong> {rec.position_size}</div>}
        {rec.stop_loss && <div><strong>Stop Loss:</strong> {rec.stop_loss}</div>}
        {rec.take_profit && <div><strong>Take Profit:</strong> {rec.take_profit}</div>}
        {rec.signals_summary && (
          <div><strong>Signals Summary:</strong> {rec.signals_summary}</div>
        )}
        {rec.key_drivers && <div><strong>Key Drivers:</strong> {rec.key_drivers}</div>}
        {rec.thesis_summary && (
          <div><strong>Thesis:</strong> {rec.thesis_summary}</div>
        )}
        {rec.trade_idea && <div><strong>Trade Idea:</strong> {rec.trade_idea}</div>}
        {rec.technical_view?.summary && (
          <div><strong>Technical View:</strong> {rec.technical_view.summary}</div>
        )}
        {rec.risk_view?.summary && (
          <div><strong>Risk View:</strong> {rec.risk_view.summary}</div>
        )}
        {rec.macro_view?.summary && (
          <div><strong>Macro View:</strong> {rec.macro_view.summary}</div>
        )}
{rec.text_reasoning && rec.text_reasoning.trim() !== "" && !isTripleLeveragedBBLRC && (
  <div className="mt-3">
    <strong>AI Reasoning:</strong>
    <div
      className="scrollable-reasoning"
      style={{
        maxHeight: "220px",
        overflowY: "auto",
        marginTop: "6px",
        padding: "10px",
        background: "#171d2a",
        borderRadius: "8px",
        border: "1px solid #2c3142",
        color: "#fff",
        fontSize: "1rem",
        lineHeight: 1.4,
      }}
    >
      {rec.text_reasoning}
    </div>
  </div>
)}
      </div>

      {/* LLM Dialog Log */}
      {data.ai_dialogue_log && (
        <>
          {dialogButton && (
            <div style={{ textAlign: "right", marginTop: "16px", marginBottom: "8px" }}>
              {dialogButton}
            </div>
          )}
          <div className="llm-dialog-wrapper">
            <LLMDialogLog dialogLog={data.ai_dialogue_log} />
          </div>
        </>
      )}

      {/* Triple Leveraged BB LRC Scale Strategy Result */}
      {isTripleLeveragedBBLRC && strategyResult && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1.5rem", backgroundColor: "#1a1d29", border: "2px solid #2c3142" }}>
          <h3 style={{ color: "#ffffff", fontSize: "1.25rem", fontWeight: "bold", marginBottom: "1rem" }}>
            Triple Leveraged BB LRC Scale Strategy
          </h3>
          
          {/* Action */}
          {strategyResult.action && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.5rem" }}>Action</div>
              <div style={{ 
                fontSize: "1.5rem", 
                fontWeight: "bold",
                color: strategyResult.action === "BUY" ? "#22c55e" :
                       strategyResult.action === "SELL" ? "#ef4444" : "#f59e0b"
              }}>
                {strategyResult.action}
              </div>
            </div>
          )}

          {/* Proposed Order */}
          {strategyResult.proposed_order && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Proposed Order</div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", lineHeight: "1.6" }}>
                <div><strong>Type:</strong> {strategyResult.proposed_order.type}</div>
                {strategyResult.proposed_order.size_pct && (
                  <div><strong>Size:</strong> {strategyResult.proposed_order.size_pct}% of allocation</div>
                )}
                {strategyResult.proposed_order.reason && (
                  <div style={{ marginTop: "0.5rem", fontStyle: "italic" }}>{strategyResult.proposed_order.reason}</div>
                )}
              </div>
            </div>
          )}

          {/* Key Levels */}
          {strategyResult.levels && Object.keys(strategyResult.levels).length > 0 && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Key Levels</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", fontSize: "0.875rem" }}>
                {strategyResult.levels.bb4h_lower !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>4h BB Lower</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.levels.bb4h_lower.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.levels.lrc4h_lower !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>4h LRC Lower</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.levels.lrc4h_lower.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.levels.lrc4h_upper !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>4h LRC Upper</div>
                    <div style={{ color: "#ffffff", fontWeight: "600" }}>${strategyResult.levels.lrc4h_upper.toFixed(2)}</div>
                  </div>
                )}
                {strategyResult.levels.pos_4h !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>LRC Position</div>
                    <div style={{ 
                      color: strategyResult.levels.pos_4h >= 0.85 ? "#ef4444" : 
                             strategyResult.levels.pos_4h >= 0.7 ? "#f59e0b" : "#10b981",
                      fontWeight: "600"
                    }}>
                      {(strategyResult.levels.pos_4h * 100).toFixed(1)}%
                    </div>
                  </div>
                )}
                {strategyResult.levels.rsi_4h !== undefined && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>RSI 4h</div>
                    <div style={{ 
                      color: strategyResult.levels.rsi_4h <= 30 ? "#10b981" :
                             strategyResult.levels.rsi_4h >= 70 ? "#ef4444" : "#9ca3af",
                      fontWeight: "600"
                    }}>
                      {strategyResult.levels.rsi_4h.toFixed(1)}
                    </div>
                  </div>
                )}
                {strategyResult.levels.next_add_level && (
                  <div>
                    <div style={{ color: "#9ca3af" }}>Next Add Level</div>
                    <div style={{ color: "#f59e0b", fontWeight: "600" }}>${strategyResult.levels.next_add_level.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analysis / Narrative */}
          {strategyResult.narrative && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Analysis</div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", lineHeight: "1.6", whiteSpace: "pre-line" }}>
                {strategyResult.narrative}
              </div>
            </div>
          )}

          {/* State Snapshot */}
          {strategyResult.state_snapshot && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.75rem" }}>Position State</div>
              <div style={{ fontSize: "0.875rem", color: "#9ca3af", lineHeight: "1.6" }}>
                <div>Tranches: {strategyResult.state_snapshot.tranches?.length || 0} / 5</div>
                {strategyResult.state_snapshot.last_buy_price && (
                  <div>Last Buy Price: ${strategyResult.state_snapshot.last_buy_price.toFixed(2)}</div>
                )}
                <div>Buys Today: {strategyResult.state_snapshot.buys_today_count || 0} / 2</div>
                {strategyResult.state_snapshot.tranches && strategyResult.state_snapshot.tranches.length > 0 && (
                  <div style={{ marginTop: "0.5rem", paddingTop: "0.5rem", borderTop: "1px solid #374151" }}>
                    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "0.25rem" }}>Tranche History:</div>
                    {strategyResult.state_snapshot.tranches.map((t: any, idx: number) => (
                      <div key={idx} style={{ fontSize: "0.7rem", color: "#6b7280" }}>
                        #{idx + 1}: {t.size_pct}% @ ${t.price?.toFixed(2) || 'N/A'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reason Codes */}
          {strategyResult.reason_codes && strategyResult.reason_codes.length > 0 && (
            <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "#2c3142", borderRadius: "8px" }}>
              <div style={{ fontSize: "0.875rem", fontWeight: "600", color: "#ffffff", marginBottom: "0.5rem" }}>Reason Codes</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {strategyResult.reason_codes.map((code: string, idx: number) => (
                  <span key={idx} style={{ 
                    padding: "0.25rem 0.5rem", 
                    backgroundColor: "#374151", 
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                    color: "#9ca3af"
                  }}>
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer with metadata */}
      <div className="card-footer-row" style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid rgba(148, 163, 184, 0.2)" }}>
        <span className="meta-muted" style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
          {data.data_provider ? `Data: ${data.data_provider}` : ''}
        </span>
      </div>
    </div>
  );
};

export default AnalysisResult;


